import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

// Lazy-load OpenLayers (~500 KB) when the localization tab opens.
import type OlMap from 'ol/Map';
import type View from 'ol/View';
import type TileLayer from 'ol/layer/Tile';
import type OSM from 'ol/source/OSM';
import type VectorLayer from 'ol/layer/Vector';
import type VectorSource from 'ol/source/Vector';
import type Cluster from 'ol/source/Cluster';
import type Feature from 'ol/Feature';
import type Point from 'ol/geom/Point';
import type { Style } from 'ol/style';
import type Overlay from 'ol/Overlay';

interface OlBundle {
  Map: typeof import('ol/Map').default;
  View: typeof import('ol/View').default;
  TileLayer: typeof import('ol/layer/Tile').default;
  OSM: typeof import('ol/source/OSM').default;
  VectorLayer: typeof import('ol/layer/Vector').default;
  VectorSource: typeof import('ol/source/Vector').default;
  Cluster: typeof import('ol/source/Cluster').default;
  Feature: typeof import('ol/Feature').default;
  Point: typeof import('ol/geom/Point').default;
  Overlay: typeof import('ol/Overlay').default;
  Zoom: typeof import('ol/control').Zoom;
  fromLonLat: typeof import('ol/proj').fromLonLat;
  Style: typeof import('ol/style').Style;
  CircleStyle: typeof import('ol/style').Circle;
  Fill: typeof import('ol/style').Fill;
  Stroke: typeof import('ol/style').Stroke;
  Text: typeof import('ol/style').Text;
}

let olBundlePromise: Promise<OlBundle> | null = null;

function loadOl(): Promise<OlBundle> {
  if (!olBundlePromise) {
    olBundlePromise = Promise.all([
      import('ol/Map'),
      import('ol/View'),
      import('ol/layer/Tile'),
      import('ol/source/OSM'),
      import('ol/layer/Vector'),
      import('ol/source/Vector'),
      import('ol/source/Cluster'),
      import('ol/Feature'),
      import('ol/geom/Point'),
      import('ol/Overlay'),
      import('ol/control'),
      import('ol/proj'),
      import('ol/style'),
    ]).then(([
      mMap, mView, mTile, mOSM, mVectorLayer, mVectorSource, mCluster,
      mFeature, mPoint, mOverlay, mControl, mProj, mStyle,
    ]) => ({
      Map: mMap.default,
      View: mView.default,
      TileLayer: mTile.default,
      OSM: mOSM.default,
      VectorLayer: mVectorLayer.default,
      VectorSource: mVectorSource.default,
      Cluster: mCluster.default,
      Feature: mFeature.default,
      Point: mPoint.default,
      Overlay: mOverlay.default,
      Zoom: mControl.Zoom,
      fromLonLat: mProj.fromLonLat,
      Style: mStyle.Style,
      CircleStyle: mStyle.Circle,
      Fill: mStyle.Fill,
      Stroke: mStyle.Stroke,
      Text: mStyle.Text,
    }));
  }
  return olBundlePromise;
}

import { AppState } from 'src/app/core/app.state';
import { selectCanDo } from 'src/app/core/store/permissions/permissions.selectors';
import { ILocation } from 'src/app/features/customers/models/customers.models';

interface LocationRow {
  location: ILocation;
  address: string;
  type: string;
  coords?: [number, number];
  geocoded: boolean;
  feature?: Feature;
}

interface MapPopupData {
  name: string;
  type: string;
  street: string;
  city: string;
  country: string;
  isMain: boolean;
  source?: 'erp' | 'volta';
}

/** Localization tab map/table; OpenLayers init deferred until `[active]` is true. */
@Component({
  selector: 'app-location-map',
  templateUrl: './location-map.component.html',
  styleUrls: ['./location-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationMapComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('olMap', { static: false }) mapElement!: ElementRef;
  @ViewChild('mapPopup', { static: false }) popupElement!: ElementRef;

  @Input() locations: ILocation[] = [];
  @Input() customerName = '';
  @Input() active = false;

  @Output() addLocation = new EventEmitter<void>();
  @Output() editLocation = new EventEmitter<ILocation>();
  @Output() deleteLocation = new EventEmitter<ILocation>();

  readonly canEdit$ = this.store.select(selectCanDo('company', 'edit'));
  readonly canDelete$ = this.store.select(selectCanDo('company', 'delete'));

  olMap!: OlMap;
  mapPopupOverlay!: Overlay;
  popupData: MapPopupData | null = null;
  mapVectorSource!: VectorSource;
  mapClusterSource!: Cluster;
  mapVectorLayer!: VectorLayer<any>;
  mapInitialized = false;
  geocodingInitialized = false;
  private ol: OlBundle | null = null;
  private olLoading: Promise<OlBundle> | null = null;

  readonly LOCATION_COLORS: string[] = [
    '#6449DB', '#E74C3C', '#2ECC71', '#F39C12', '#3498DB',
    '#9B59B6', '#1ABC9C', '#E67E22', '#34495E', '#16A085',
  ];
  locationTypeCounts: { type: string; color: string; count: number; visible: boolean }[] = [];

  isLocationTableCollapsed = false;
  locationSearchInput = new FormControl('');
  sourceFilter: 'all' | 'erp' | 'volta' = 'all';
  selectedLocationId: number | null = null;
  readonly locBatchSize = 20;
  locVisibleCount = 20;
  isLoadingMore = false;
  locationRows: LocationRow[] = [];
  filteredLocations: LocationRow[] = [];
  private locationTypeColorMap = new globalThis.Map<string, string>();
  private geocodeCache = new globalThis.Map<string, [number, number] | null>();
  private geocodeQueue: LocationRow[] = [];
  private geocodeRunning = false;
  private readonly GEOCODE_DELAY_MS = 1100;
  private readonly SEARCH_DEBOUNCE_MS = 250;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    private http: HttpClient,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.locationSearchInput.valueChanges
      .pipe(
        debounceTime(this.SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.applyLocationFilter();
        this.cdr.markForCheck();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['locations'] && this.locations) {
      this.rebuildLocationRows();
    }
    if (changes['active'] && this.active) {
      setTimeout(async () => {
        if (!this.mapElement) return;
        if (!this.mapInitialized) {
          await this.initMap();
        }
        this.olMap?.updateSize();
      }, 100);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.olMap) {
      this.olMap.setTarget(undefined as any);
    }
  }

  get isGeocoding(): boolean {
    return this.geocodeRunning;
  }

  get visibleLocations(): LocationRow[] {
    return this.filteredLocations.slice(0, this.locVisibleCount);
  }

  get hasMoreLocations(): boolean {
    return this.locVisibleCount < this.filteredLocations.length;
  }

  trackLocationRow = (_: number, row: LocationRow): number | string =>
    row.location.id ?? row.location.name;

  async initMap(): Promise<void> {
    if (this.mapInitialized || !this.mapElement) return;
    this.mapInitialized = true;

    const ol = await this.ensureOl();
    if (!this.mapElement) return; // tab/component torn down while loading

    const {
      Map: OlMapCtor, View, TileLayer, OSM,
      VectorLayer, VectorSource, Cluster, Overlay,
      Zoom, fromLonLat,
    } = ol;

    if (!this.mapVectorSource) {
      this.mapVectorSource = new VectorSource();
    }

    const popupEl = this.popupElement?.nativeElement;
    this.mapPopupOverlay = new Overlay({
      element: popupEl,
      positioning: 'bottom-center' as any,
      offset: [0, -14],
      stopEvent: false,
    });

    this.mapClusterSource = new Cluster({
      source: this.mapVectorSource,
      distance: 40,
      minDistance: 20,
    });

    this.mapVectorLayer = new VectorLayer({
      source: this.mapClusterSource,
      style: (feature) => this.clusterStyleFn(feature as Feature),
    });

    this.olMap = new OlMapCtor({
      target: this.mapElement.nativeElement,
      layers: [
        new TileLayer({ source: new OSM() }),
        this.mapVectorLayer,
      ],
      overlays: [this.mapPopupOverlay],
      view: new View({
        center: fromLonLat([2.3522, 48.8566]),
        zoom: 5,
      }),
      controls: [new Zoom()],
    });

    this.olMap.on('click', (evt) => {
      const clicked = this.olMap.forEachFeatureAtPixel(evt.pixel, (f) => f);
      if (!clicked) {
        this.closeMapPopup();
        return;
      }
      const inner: Feature[] = (clicked.get('features') as Feature[]) || [];
      if (inner.length > 1) {
        const extent = inner
          .map(f => (f.getGeometry() as Point).getCoordinates())
          .reduce(
            (ext, c) => [
              Math.min(ext[0], c[0]),
              Math.min(ext[1], c[1]),
              Math.max(ext[2], c[0]),
              Math.max(ext[3], c[1]),
            ],
            [Infinity, Infinity, -Infinity, -Infinity],
          );
        this.olMap.getView().fit(extent as any, { duration: 350, padding: [60, 60, 60, 60], maxZoom: 18 });
        return;
      }
      const single = inner.length === 1 ? inner[0] : (clicked as Feature);
      const coords = (single.getGeometry() as Point).getCoordinates();
      this.ngZone.run(() => {
        this.popupData = single.get('locationData');
        const locId = single.get('locationId');
        this.selectedLocationId = typeof locId === 'number' ? locId : this.selectedLocationId;
        this.cdr.markForCheck();
      });
      this.mapPopupOverlay.setPosition(coords);
    });

    this.olMap.on('pointermove', (evt) => {
      const hit = this.olMap.forEachFeatureAtPixel(evt.pixel, () => true);
      this.olMap.getTargetElement().style.cursor = hit ? 'pointer' : '';
    });

    this.cdr.markForCheck();
  }

  private async ensureOl(): Promise<OlBundle> {
    if (this.ol) return this.ol;
    if (!this.olLoading) {
      this.olLoading = loadOl().then((bundle) => {
        this.ol = bundle;
        return bundle;
      });
    }
    return this.olLoading;
  }

  closeMapPopup(): void {
    this.mapPopupOverlay?.setPosition(undefined);
    this.popupData = null;
    this.cdr.markForCheck();
  }

  toggleLocationTable(): void {
    this.isLocationTableCollapsed = !this.isLocationTableCollapsed;
    this.cdr.markForCheck();
    setTimeout(() => this.olMap?.updateSize(), 280);
  }

  loadMoreLocations(): void {
    if (this.isLoadingMore || !this.hasMoreLocations) return;
    this.isLoadingMore = true;
    this.cdr.markForCheck();
    setTimeout(() => {
      this.locVisibleCount = Math.min(
        this.locVisibleCount + this.locBatchSize,
        this.filteredLocations.length,
      );
      this.isLoadingMore = false;
      this.cdr.markForCheck();
    }, 300);
  }

  onLocationsScroll(event: Event): void {
    if (this.isLoadingMore || !this.hasMoreLocations) return;
    const target = event.target as HTMLElement;
    const scrollPosition = target.scrollTop + target.clientHeight;
    const scrollHeight = target.scrollHeight;
    if (scrollHeight > 0 && scrollPosition / scrollHeight > 0.8) {
      this.loadMoreLocations();
    }
  }

  onLocationRowClick(row: LocationRow): void {
    this.selectedLocationId = row.location.id ?? null;
    this.cdr.markForCheck();
    if (!this.mapInitialized) {
      setTimeout(async () => {
        if (!this.mapElement) return;
        if (!this.mapInitialized) await this.initMap();
        this.olMap?.updateSize();
        this.handleRowFocus(row);
      }, 100);
      return;
    }
    this.handleRowFocus(row);
  }

  private handleRowFocus(row: LocationRow): void {
    if (row.geocoded && row.coords) {
      this.focusOnRow(row);
      return;
    }
    this.geocodeRow(row).then(() => {
      if (row.geocoded && row.coords) {
        this.focusOnRow(row);
      }
    });
  }

  private focusOnRow(row: LocationRow): void {
    if (!row.coords || !this.olMap || !this.ol) return;
    const center = this.ol.fromLonLat(row.coords);
    this.olMap.getView().animate({ center, zoom: 16, duration: 400 });
    if (row.feature) {
      this.ngZone.run(() => {
        this.popupData = row.feature!.get('locationData');
        this.cdr.markForCheck();
      });
      this.mapPopupOverlay?.setPosition(center);
    }
  }

  rebuildLocationRows(): void {
    const all = this.locations || [];
    this.locationRows = all.map((loc) => {
      const address = loc.shipping_address || this.buildAddressString(loc);
      const type = this.getLocationType(loc);
      return { location: loc, address, type, geocoded: false };
    });
    this.applyLocationFilter();

    if (!this.geocodingInitialized) {
      this.startLazyGeocoding();
      this.geocodingInitialized = true;
    } else if (this.mapInitialized) {
      this.mapVectorSource.clear();
      this.locationTypeColorMap.clear();
      this.locationTypeCounts = [];
      this.locationRows.forEach(r => { r.geocoded = false; r.feature = undefined; });
      this.startLazyGeocoding();
    }
  }

  setSourceFilter(filter: 'all' | 'erp' | 'volta'): void {
    this.sourceFilter = filter;
    this.applyLocationFilter();
  }

  applyLocationFilter(): void {
    const term = (this.locationSearchInput.value || '').toString().toLowerCase().trim();
    this.filteredLocations = this.locationRows.filter((row) => {
      const matchesSource =
        this.sourceFilter === 'all' ||
        (row.location.provenance?.source ?? 'erp') === this.sourceFilter;
      const matchesSearch =
        !term ||
        (row.location.name || '').toLowerCase().includes(term) ||
        (row.address || '').toLowerCase().includes(term);
      return matchesSource && matchesSearch;
    });
    this.locVisibleCount = this.locBatchSize;
    this.isLoadingMore = false;
    this.cdr.markForCheck();
  }

  private buildAddressString(location: ILocation): string {
    const parts: string[] = [];
    if (location.apartment) parts.push(location.apartment);
    if (location.city) parts.push(location.city);
    if (location.state?.name) parts.push(location.state.name);
    if (location.zip) parts.push(location.zip);
    if (location.country?.name) parts.push(location.country.name);
    return parts.join(', ');
  }

  private getLocationType(location: ILocation): string {
    if (location.is_main) return 'Main Location';
    if (location.shipping_address) return 'Shipping Adress';
    return 'Branch';
  }


  private startLazyGeocoding(): void {
    if (!this.locationRows.length) return;
    this.geocodeQueue = this.locationRows.filter(r => !r.geocoded && !!r.address);
    if (!this.geocodeRunning) {
      this.processGeocodeQueue();
    }
  }

  private async processGeocodeQueue(): Promise<void> {
    this.geocodeRunning = true;
    this.cdr.markForCheck();
    const ol = await this.ensureOl();
    if (!this.mapVectorSource) {
      this.mapVectorSource = new ol.VectorSource();
    }
    while (this.geocodeQueue.length > 0) {
      const next = this.geocodeQueue.shift()!;
      if (next.geocoded) continue;
      await this.geocodeRow(next);
      if (this.geocodeQueue.length > 0) {
        await this.delay(this.GEOCODE_DELAY_MS);
      }
    }
    this.geocodeRunning = false;
    this.cdr.markForCheck();
    this.fitMapToFeatures();
  }

  private async geocodeRow(row: LocationRow): Promise<void> {
    if (row.geocoded || !row.address) return;
    const cached = this.geocodeCache.get(row.address);
    if (cached === null) {
      this.ngZone.run(() => {
        row.geocoded = false;
        this.cdr.markForCheck();
      });
      return;
    }
    let coords: [number, number] | null = cached ?? null;
    if (!cached) {
      coords = await this.geocodeAddress(row.address);
      this.geocodeCache.set(row.address, coords);
    }
    if (!coords) return;

    const ol = await this.ensureOl();
    if (!this.mapVectorSource) {
      this.mapVectorSource = new ol.VectorSource();
    }
    row.coords = coords;
    const color = this.getColorForType(row.type);
    const feature = new ol.Feature({ geometry: new ol.Point(ol.fromLonLat(coords)) });
    feature.set('locationData', {
      name: row.location.name || this.customerName || '',
      type: row.type,
      street: row.location.apartment || '',
      city: row.location.city || '',
      country: row.location.country?.name || '',
      isMain: row.location.is_main,
      source: row.location.provenance?.source,
    });
    feature.set('locationType', row.type);
    feature.set('locationId', row.location.id);
    row.feature = feature;
    this.mapVectorSource.addFeature(feature);

    this.ngZone.run(() => {
      row.geocoded = true;
      const entry = this.locationTypeCounts.find(t => t.type === row.type);
      if (entry) {
        entry.count += 1;
      } else {
        this.locationTypeCounts = [
          ...this.locationTypeCounts,
          { type: row.type, color, count: 1, visible: true },
        ];
      }
      this.cdr.markForCheck();
    });
  }

  private fitMapToFeatures(): void {
    if (!this.olMap || this.mapVectorSource.getFeatures().length === 0) return;
    const extent = this.mapVectorSource.getExtent();
    if (!extent || !isFinite(extent[0])) return;
    this.olMap.getView().fit(extent, { padding: [60, 60, 60, 60], maxZoom: 14, duration: 500 });
  }

  private getColorForType(type: string): string {
    if (this.locationTypeColorMap.has(type)) return this.locationTypeColorMap.get(type)!;
    const idx = this.locationTypeColorMap.size % this.LOCATION_COLORS.length;
    const color = this.LOCATION_COLORS[idx];
    this.locationTypeColorMap.set(type, color);
    return color;
  }

  private clusterStyleFn(feature: Feature): Style {
    const ol = this.ol!;
    const inner: Feature[] = (feature.get('features') as Feature[]) || [];
    if (inner.length > 1) {
      return new ol.Style({
        image: new ol.CircleStyle({
          radius: 14 + Math.min(10, Math.log2(inner.length) * 2),
          fill: new ol.Fill({ color: 'rgba(100, 73, 219, 0.85)' }),
          stroke: new ol.Stroke({ color: '#fff', width: 2 }),
        }),
        text: new ol.Text({
          text: inner.length.toString(),
          fill: new ol.Fill({ color: '#fff' }),
          font: 'bold 12px Poppins, sans-serif',
        }),
      });
    }
    const single = inner[0] || feature;
    const type: string = single.get('locationType') || 'Branch';
    const entry = this.locationTypeCounts.find(t => t.type === type);
    if (entry && !entry.visible) return new ol.Style({});
    const color = entry?.color || this.getColorForType(type);
    return this.createMarkerStyle(color);
  }

  private delay(ms: number): Promise<void> {
    return new Promise((res) => setTimeout(res, ms));
  }

  private async geocodeAddress(address: string): Promise<[number, number] | null> {
    const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(address)}`;
    try {
      const results = await this.http.get<any[]>(url, {
        headers: { 'Accept-Language': 'en' },
      }).toPromise();
      if (results?.length) {
        return [parseFloat(results[0].lon), parseFloat(results[0].lat)];
      }
      console.warn(`Geocoding: no results for "${address}"`);
      return null;
    } catch (err) {
      console.warn(`Geocoding error for "${address}":`, err);
      return null;
    }
  }

  private createMarkerStyle(color: string): Style {
    const ol = this.ol!;
    return new ol.Style({
      image: new ol.CircleStyle({
        radius: 8,
        fill: new ol.Fill({ color }),
        stroke: new ol.Stroke({ color: '#fff', width: 2 }),
      }),
    });
  }

  toggleLocationType(type: string): void {
    const entry = this.locationTypeCounts.find(t => t.type === type);
    if (!entry) return;
    entry.visible = !entry.visible;
    this.mapClusterSource?.changed();
    this.mapVectorLayer?.changed();
    this.cdr.markForCheck();
  }
}
